import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import withSuspense from "helpers/withSuspense";
import { PageLoading } from "helpers/loading";
import OauthVerify from "./settings/emailconfig/OauthVerify";
import Login from "./login/Login";
import Logout from "./login/Logout";
import TisRegister from "./login/TisRegister";
import SwitchInstance from "./login/SwitchInstance";
import ForgotPassword from "./login/password/ForgotPassword";
import ResetPassword from "./login/password/ResetPassword";
import { FooterContextProvider } from "../components/footer/FooterContext";
import { PageContentContextProvider } from "../components/pageContent/PageContentContext";
import PlayBorder from "../components/play/PlayBorder";
import { GeolocationProvider } from "helpers/GeolocationContext";
import GAListener from "./GoogleGa";
import "./handle401";
import { addGoogleTagManager } from "components/utils/InitiateScript";
import DeploymentUpdateBanner from "components/deploymentUpdate/DeploymentUpdateBanner";
import ChunkErrorBoundary from "components/errorBoundary/ChunkErrorBoundary";
import lazyWithReload from "helpers/lazyWithReload";
import { installChunkErrorListeners } from "helpers/chunkReload";

installChunkErrorListeners();

const Layout = withSuspense(
  lazyWithReload(() => import("../components/uis/layout/Layout")),
  PageLoading
);

const RentalBoardRoutes = withSuspense(
  lazyWithReload(() => import("./RentalBoardRoutes")),
  PageLoading
);

const SignatureRequested = withSuspense(
  lazyWithReload(() => import("./contracts/signature/SignatureRequested")),
  PageLoading
);

const PaymentScreenRequested = withSuspense(
  lazyWithReload(() => import("./payment/PaymentScreen")),
  PageLoading
);

const DocumentPaymentRequested = withSuspense(
  lazyWithReload(() => import("./transactions/payment/DocumentPaymentRequested")),
  PageLoading
);

const CalendarPresent = withSuspense(
  lazyWithReload(() => import("./dashboard/calendar/Present")),
  PageLoading
);

const rawAppBasePath = process.env.APP_BASE_PATH || "";
const normalizedAppBasePath = rawAppBasePath.replace(/^\/+|\/+$/g, "");
const appBasePath = normalizedAppBasePath ? `/${normalizedAppBasePath}` : "";
const routerBasename =
  appBasePath &&
  (window.location.pathname === appBasePath ||
    window.location.pathname.startsWith(`${appBasePath}/`))
    ? appBasePath
    : "/";

const router = createBrowserRouter([
  {
    element: (
      <FooterContextProvider>
        <PageContentContextProvider>
          <GAListener />
          <PlayBorder />
          <GeolocationProvider>
            <Outlet />
          </GeolocationProvider>
        </PageContentContextProvider>
      </FooterContextProvider>
    ),
    children: [
      { path: "/forgetpassword", element: <ForgotPassword /> },
      { path: "/oauth/verify", element: <OauthVerify /> },
      { path: "/fg/:id", element: <ResetPassword /> },
      { path: "/", element: <Login /> },
      { path: "/company/:tenantId", element: <Login /> },
      { path: "/mobile/:token", element: <Login /> },
      { path: "/verify", element: <Login /> },
      { path: "/register", element: <TisRegister /> },
      { path: "/logout", element: <Logout /> },
      { path: "/instance/:id", element: <SwitchInstance /> },
      { path: "/inventory/rentalBoard/*", element: <RentalBoardRoutes /> },
      { path: "/payment", element: <PaymentScreenRequested /> },
      {
        path: "/contract_signature_requested/:id",
        element: <SignatureRequested />
      },
      {
        path: "/reservation_signature_requested/:id",
        element: <SignatureRequested />
      },
      {
        path: "/quote_signature_requested/:id",
        element: <SignatureRequested />
      },
      {
        path: "/all-in-one-transaction_signature_requested/:id",
        element: <SignatureRequested />
      },
      {
        path: "/payment_requested/:documentType/:paymentId",
        element: <DocumentPaymentRequested />
      },
      { path: "/dashboard/calendar/present", element: <CalendarPresent /> },
      { path: "*", element: <Layout /> }
    ]
  }
], {
  basename: routerBasename
});

const App = () => {
  useEffect(() => {
    addGoogleTagManager();
  }, []);

  return (
    <>
      <DeploymentUpdateBanner />
      <ChunkErrorBoundary>
        <RouterProvider router={router} />
      </ChunkErrorBoundary>
    </>
  );
};

export default App;
